/**
* Meta CAPI Event Tracking Utility
* Sends events to n8n webhook for Meta Conversions API
* Tracks: PageViews, UTMs, Sessions, and custom events
*
* Supported user_data parameters for match quality:
* - fbc: Click ID (Facebook Click ID from URL or cookie)
* - fbp: Browser ID (from _fbp cookie)
* - email: Hashed user email
* - phone: Hashed user phone
* - external_id: Hashed user ID
* - zip: Hashed zip/postal code
* - fn: Hashed first name
* - ln: Hashed last name
* - ct: Hashed city
* - st: Hashed state
* - dob: Hashed date of birth
* - country: Hashed country code
*/

// ── Storage Keys ────────────────────────────────────────────────────────

const SESSION_KEY = 'ndx_session_id';
const UTM_KEY = 'ndx_utm_data';
const CUSTOMER_KEY = 'ndx_customer_data';

// ── SHA256 Hashing Helper ───────────────────────────────────────────────

/**
* Hash a value using SHA256 (required by Meta for PII)
* @param {string} value - Value to hash
* @returns {Promise<string>} - Lowercase hex hash
*/
async function sha256Hash(value) {
if (!value) return null;
// Normalize: lowercase, trim whitespace
const normalized = value.toString().toLowerCase().trim();
const encoder = new TextEncoder();
const data = encoder.encode(normalized);
const hashBuffer = await crypto.subtle.digest('SHA-256', data);
const hashArray = Array.from(new Uint8Array(hashBuffer));
return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── Session & UTM Storage ────────────────────────────────────────────────

/** Generate or retrieve a persistent session ID (lifetime: 30 days) */
function getOrCreateSessionId() {
let sessionId = sessionStorage.getItem(SESSION_KEY);
if (!sessionId) {
sessionId = crypto.randomUUID();
sessionStorage.setItem(SESSION_KEY, sessionId);
}
return sessionId;
}

/** Extract UTM params from URL and store in sessionStorage */
function captureUTM() {
const stored = sessionStorage.getItem(UTM_KEY);
if (stored) return JSON.parse(stored);

const params = new URLSearchParams(window.location.search);
const utmData = {
utm_source: params.get('utm_source') || null,
utm_medium: params.get('utm_medium') || null,
utm_campaign: params.get('utm_campaign') || null,
utm_term: params.get('utm_term') || null,
utm_content: params.get('utm_content') || null,
};

const hasUTM = Object.values(utmData).some(v => v !== null);
if (hasUTM) {
sessionStorage.setItem(UTM_KEY, JSON.stringify(utmData));
}
return utmData;
}

// ── Customer Data Storage ────────────────────────────────────────────────

/**
* Store customer data for enhanced match quality
* Call this when user fills a form (contact, booking, etc.)
* @param {Object} data - Customer information
*/
export async function setCustomerData(data) {
const customerData = {
email: data.email || null,
phone: data.phone || null,
first_name: data.first_name || data.firstName || null,
last_name: data.last_name || data.lastName || null,
zip: data.zip || data.postal_code || null,
city: data.city || null,
state: data.state || null,
country: data.country || 'eg', // Default to Egypt
dob: data.dob || data.date_of_birth || null,
external_id: data.external_id || data.userId || null,
};
sessionStorage.setItem(CUSTOMER_KEY, JSON.stringify(customerData));
return customerData;
}

/**
* Retrieve stored customer data
* @returns {Object|null} Customer data or null
*/
function getCustomerData() {
const stored = sessionStorage.getItem(CUSTOMER_KEY);
return stored ? JSON.parse(stored) : null;
}

/** Get cookie value by name */
function getCookie(name) {
const value = `; ${document.cookie}`;
const parts = value.split(`; ${name}=`);
if (parts.length === 2) return parts.pop().split(';').shift();
return null;
}

/**
* Extract fbc (Facebook Click ID) from:
* 1. URL fbclid parameter (most reliable)
* 2. _fbc cookie (fallback)
* Format: fb.{version}.{click_time}.{fbclid}
*/
function getFbc() {
// Check URL for fbclid first
const params = new URLSearchParams(window.location.search);
const fbclid = params.get('fbclid');
if (fbclid) {
// Build fbc format: fb.{version}.{timestamp}.{fbclid}
const timestamp = Math.floor(Date.now() / 1000);
const fbc = `fb.1.${timestamp}.${fbclid}`;
// Store in cookie for persistence
document.cookie = `_fbc=${fbc}; path=/; max-age=7776000; SameSite=Lax`; // 90 days
return fbc;
}
// Fallback to cookie
return getCookie('_fbc');
}

// ── Core Event Sender ────────────────────────────────────────────────────

const WEBHOOK_URL = import.meta.env.VITE_META_CAPI_WEBHOOK_URL;
// Note: No auth credentials in client code — they would be exposed in the JS bundle.
// Secure the n8n webhook using one of:
//   1. A secret token in the URL path (n8n → Webhook node → Path: /meta-capi/your-secret-token)
//   2. n8n's built-in Basic Auth header check (configured in the Webhook node, not client-side)
//   3. Origin header validation in n8n (only accept requests from nodatx.com)

const isConfigured = !!(WEBHOOK_URL);

/**
* Send an event to Meta CAPI webhook + Pixel (both in parallel)
* @param {string} eventName - e.g. 'PageView', 'Lead', 'Contact'
* @param {Object} customData - additional fields under custom_data
* @param {Object} userDataOverrides - additional user_data fields (will be hashed)
* @param {Object} options - Additional options { skipHashing: boolean }
*/
export async function sendMetaEvent(eventName, customData = {}, userDataOverrides = {}, options = {}) {
const sessionId = getOrCreateSessionId();
const utmData = captureUTM();
const customerData = getCustomerData();
const fbc = getFbc();
const fbp = getCookie('_fbp');

// Build base user_data
const baseUserData = {
client_user_agent: navigator.userAgent,
client_ip_address: 'REDACTED', // Will be filled by server/n8n
fbp: fbp || undefined,
fbc: fbc || undefined,
session_id: sessionId,
url: window.location.href,
page_title: document.title || null,
// UTM data
utm_source: utmData.utm_source || undefined,
utm_medium: utmData.utm_medium || undefined,
utm_campaign: utmData.utm_campaign || undefined,
utm_term: utmData.utm_term || undefined,
utm_content: utmData.utm_content || undefined,
};

// Add customer data if available (with hashing)
if (customerData) {
const { skipHashing } = options;

// Hash PII fields if not already hashed
const hashedData = {};
if (customerData.email) {
hashedData.em = skipHashing ? customerData.email : await sha256Hash(customerData.email);
}
if (customerData.phone) {
hashedData.ph = skipHashing ? customerData.phone : await sha256Hash(customerData.phone);
}
if (customerData.first_name) {
hashedData.fn = skipHashing ? customerData.first_name : await sha256Hash(customerData.first_name);
}
if (customerData.last_name) {
hashedData.ln = skipHashing ? customerData.last_name : await sha256Hash(customerData.last_name);
}
if (customerData.zip) {
hashedData.zp = skipHashing ? customerData.zip : await sha256Hash(customerData.zip);
}
if (customerData.city) {
hashedData.ct = skipHashing ? customerData.city : await sha256Hash(customerData.city);
}
if (customerData.state) {
hashedData.st = skipHashing ? customerData.state : await sha256Hash(customerData.state);
}
if (customerData.country) {
hashedData.country = skipHashing ? customerData.country : await sha256Hash(customerData.country);
}
if (customerData.dob) {
// Format: YYYYMMDD
const dobFormatted = customerData.dob.replace(/[-/]/g, '');
hashedData.dob = skipHashing ? dobFormatted : await sha256Hash(dobFormatted);
}
if (customerData.external_id) {
hashedData.external_id = skipHashing ? customerData.external_id : await sha256Hash(customerData.external_id);
}

Object.assign(baseUserData, hashedData);
}

// Merge with overrides (these should already be hashed if needed)
const userData = { ...baseUserData, ...userDataOverrides };

// Remove undefined values to clean payload
Object.keys(userData).forEach(key => {
if (userData[key] === undefined || userData[key] === null) {
delete userData[key];
}
});

const payload = {
event_name: eventName,
event_time: Math.floor(Date.now() / 1000),
event_source_url: window.location.href,
event_id: `${sessionId}-${eventName}-${Date.now()}`,
action_source: 'website',
user_data: userData,
custom_data: {
page_path: window.location.pathname,
page_url: window.location.href,
...customData,
},
};

// Skip webhook in development to avoid CORS errors
  const isDev = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  if (isDev) {
    console.debug('[Meta CAPI] Skipping webhook in development:', eventName);
    return;
  }
  
  if (!isConfigured) {
    return;
  }

  // Fire to webhook
  fetch(WEBHOOK_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {}); // silent
}

// ── Automatic PageView Tracking ──────────────────────────────────────────

/** Fire PageView event — call once on initial load */
export function trackPageView() {
    sendMetaEvent('PageView', {
        content_name: document.title,
        page_path: window.location.pathname,
    });
}

/** Fire PageView on React Router route changes */
export function setupRouteTracking() {
    // Listen for history changes (works with react-router-dom v5 & v6)
    const handleRouteChange = () => {
        // Small delay to let React finish rendering
        setTimeout(() => {
            trackPageView();
        }, 100);
    };

    // For react-router-dom v6
    if (typeof window !== 'undefined') {
        window.addEventListener('popstate', handleRouteChange);

        // Intercept pushState/replaceState for programmatic navigation
        const originalPushState    = window.history.pushState;
        const originalReplaceState = window.history.replaceState;

        window.history.pushState = function(...args) {
            originalPushState.apply(this, args);
            handleRouteChange();
        };
        window.history.replaceState = function(...args) {
            originalReplaceState.apply(this, args);
            handleRouteChange();
        };
    }
}

// ── Track Initial DOM Load ───────────────────────────────────────────────

if (typeof window !== 'undefined') {
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            setTimeout(trackPageView, 300); // slight delay for SPA hydration
        });
    } else {
        // DOM already ready
        setTimeout(trackPageView, 300);
    }
}

// ── Named convenience wrappers (backwards-compatible) ─────────────────────

export const sendMetaCapEvent = sendMetaEvent;
